// Discord Activity build (REACT_APP_PORTAL=discord). The game runs inside Discord in an iframe
// served through Discord's proxy, so every outside address has to go through a URL mapping.
// Players sign in with their Discord account (no Rafiki sign-up needed) and everyone in the
// same voice call lands in the same room.
import firebase from "./fb";
import { isDiscordBuild } from "./portal";

export const isDiscord = isDiscordBuild;
const CLIENT_ID = process.env.REACT_APP_DISCORD_CLIENT_ID || "";

let sdk: any = null;
// The SDK lives in its own file that only the Discord build ever downloads.
let sdkModule: Promise<any> | null = null;
const loadSdk = (): Promise<any> => {
  if (!sdkModule) sdkModule = import(/* webpackChunkName: "discord-sdk" */ "./vendor/discord-sdk");
  return sdkModule;
};

// Route outside requests through Discord's proxy. Started as the app loads, finished before sign-in.
let patched: Promise<void> | null = null;
export const patchDiscordUrls = (): Promise<void> => {
  if (!patched) patched = loadSdk().then((m) => m.patchUrlMappings(DISCORD_URL_MAPPINGS));
  return patched;
};
let started: Promise<string> | null = null;

// The mappings must match the ones set in the Discord Developer Portal (Activities > URL Mappings).
export const DISCORD_URL_MAPPINGS = [
  { prefix: "/fs", target: "firestore.googleapis.com" },
  { prefix: "/idt", target: "identitytoolkit.googleapis.com" },
  { prefix: "/st", target: "securetoken.googleapis.com" },
  { prefix: "/fn", target: "europe-west2-rafiki-d956b.cloudfunctions.net" },
  { prefix: "/gapis", target: "www.googleapis.com" },
  { prefix: "/gfonts", target: "fonts.googleapis.com" },
  { prefix: "/gstatic", target: "fonts.gstatic.com" },
];

// A short, stable room code for this Discord activity instance, so everyone in the call shares a table.
export const roomCodeForInstance = (instanceId: string) => {
  let h = 0;
  for (let i = 0; i < instanceId.length; i++) h = (h * 31 + instanceId.charCodeAt(i)) >>> 0;
  return "d" + h.toString(36);
};

// Connects to Discord, signs the player in to Firebase and returns this call's room code.
export const startDiscord = (): Promise<string> => {
  if (!isDiscord) return Promise.reject(new Error("Not a Discord build"));
  if (started) return started;
  started = (async () => {
    await patchDiscordUrls();
    const { DiscordSDK } = await loadSdk();
    sdk = new DiscordSDK(CLIENT_ID);
    await sdk.ready();
    const { code } = await sdk.commands.authorize({
      client_id: CLIENT_ID,
      response_type: "code",
      state: "",
      prompt: "none",
      scope: ["identify"],
    });
    const result: any = await firebase.app().functions("europe-west2").httpsCallable("discordToken")({ code });
    const { token, accessToken } = result?.data || {};
    if (!token) throw new Error("Couldn't sign in with Discord");
    await firebase.auth().signInWithCustomToken(token);
    if (accessToken) await sdk.commands.authenticate({ access_token: accessToken }).catch(() => undefined);
    return roomCodeForInstance(sdk.instanceId);
  })();
  started.catch(() => {
    started = null; // allow a retry
  });
  return started;
};
